import React, { useState } from 'react';
import { Play, Pause, RotateCcw, SkipForward, Maximize2, Volume2, ChevronRight, Check, Target, Lock } from 'lucide-react';
import { useTasks, useTimerStore, PRIORITY_COLORS } from '@flowfocus/core';
import { useTimer } from '../hooks/TimerContext';
import { Sheet } from '../components/ui';
import { WhiteNoisePanel } from '../components/WhiteNoisePanel';

export const SESSION = {
  work:       { label: 'Enfoque',        color: 'var(--primary)' },
  shortBreak: { label: 'Descanso',       color: 'var(--green)' },
  longBreak:  { label: 'Descanso largo', color: '#2f6fb0' },
} as const;

export function TimerPage({ uid }: { uid: string }) {
  const t = useTimer();
  const settings = useTimerStore((s) => s.settings);
  const wn = settings.whiteNoise;
  const { tasks } = useTasks(uid);
  const [showNoise, setShowNoise] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const running = t.status === 'running';
  const paused = t.status === 'paused';
  const session = SESSION[t.sessionType];
  const strictLock = settings.strictMode && running;
  const activeTask = tasks.find((x) => x.id === t.taskId);
  const pending = tasks.filter((x) => !x.completed);
  const noiseOn = wn.enabled && Object.values(wn.sounds).some((v) => v > 0);

  const R = 100, C = 116, circ = 2 * Math.PI * R;
  const offset = circ * (1 - t.progress);
  const onPlay = () => (running ? t.pause() : paused ? t.resume() : t.start(t.taskId));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 26px 0' }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.5px', margin: 0 }}>Temporizador</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="icon-btn" title="Sonidos" onClick={() => setShowNoise(true)} style={noiseOn ? { background: 'var(--primary-soft)', color: 'var(--primary)' } : undefined}><Volume2 size={17} /></button>
          <button className="icon-btn" title="Pantalla completa" onClick={t.openFullscreen}><Maximize2 size={16} /></button>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px 24px 40px', maxWidth: 460, width: '100%', margin: '0 auto' }}>
        <div style={{ width: '100%', marginBottom: 26, opacity: running ? 0.5 : 1, pointerEvents: running ? 'none' : 'auto' }}>
          <div className="seg">
            {(['work', 'shortBreak', 'longBreak'] as const).map((s) => (
              <button key={s} className={t.sessionType === s ? 'on' : ''} onClick={() => t.setSession(s)}>{SESSION[s].label}</button>
            ))}
          </div>
        </div>

        <div style={{ position: 'relative', width: 232, height: 232, marginBottom: 10 }}>
          <svg width={232} height={232} viewBox="0 0 232 232">
            <circle cx={C} cy={C} r={R} fill="none" stroke="var(--fill)" strokeWidth={10} />
            <circle cx={C} cy={C} r={R} fill="none" stroke={session.color} strokeWidth={10} strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset} transform={`rotate(-90 ${C} ${C})`} style={{ transition: 'stroke-dashoffset 0.5s linear' }} />
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 56, fontWeight: 300, letterSpacing: '-1.5px', fontVariantNumeric: 'tabular-nums' }}>{t.display}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: session.color, textTransform: 'uppercase', letterSpacing: '0.07em', marginTop: 2 }}>{running ? 'En curso' : paused ? 'En pausa' : session.label}</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 7, marginBottom: 22 }}>
          {Array.from({ length: settings.longBreakAfter }).map((_, i) => (
            <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: i < t.pomodorosThisSet ? session.color : 'var(--fill)' }} />
          ))}
        </div>

        <button onClick={() => setShowPicker(true)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 11, background: 'var(--bg-elev)', border: '0.5px solid var(--border)', borderRadius: 13, padding: '13px 15px', cursor: 'pointer', marginBottom: 24 }}>
          <Target size={17} color={activeTask ? 'var(--primary)' : 'var(--text-mut)'} />
          <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
            <div style={{ fontSize: 10, color: 'var(--text-mut)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Enfocando</div>
            <div style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeTask ? activeTask.name : 'Elegir una tarea…'}</div>
          </div>
          {activeTask && <span className="chip" style={{ background: 'var(--primary-soft)', color: 'var(--primary)' }}>{activeTask.completedPomodoros}/{activeTask.estimatedPomodoros}</span>}
          <ChevronRight size={17} color="var(--text-mut)" />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 30 }}>
          <button className="icon-btn" onClick={t.reset} disabled={strictLock} style={{ opacity: strictLock ? 0.3 : 1, width: 48, height: 48 }} title="Reiniciar (R)"><RotateCcw size={20} /></button>
          <button onClick={onPlay} style={{ width: 80, height: 80, borderRadius: '50%', border: 'none', cursor: 'pointer', background: session.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 10px 28px ${session.color === 'var(--primary)' ? 'rgba(10,23,48,0.4)' : 'rgba(0,0,0,0.22)'}` }}>
            {running ? <Pause size={32} fill="#fff" /> : <Play size={32} fill="#fff" style={{ marginLeft: 3 }} />}
          </button>
          <button className="icon-btn" onClick={t.skip} disabled={strictLock} style={{ opacity: strictLock ? 0.3 : 1, width: 48, height: 48 }} title="Saltar (S)"><SkipForward size={20} /></button>
        </div>

        {strictLock && <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 16, color: 'var(--text-mut)', fontSize: 12 }}><Lock size={13} /> Modo estricto activo</div>}
      </div>

      {showNoise && <Sheet title="Sonidos de fondo" onClose={() => setShowNoise(false)}><WhiteNoisePanel /></Sheet>}

      {showPicker && (
        <Sheet title="Enfocar en…" onClose={() => setShowPicker(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 380, overflowY: 'auto' }}>
            <button onClick={() => { t.setTask(undefined); setShowPicker(false); }} style={pickerRow}>
              <span style={{ flex: 1, textAlign: 'left', color: 'var(--text-mut)' }}>Sin tarea</span>
              {!t.taskId && <Check size={16} color="var(--primary)" />}
            </button>
            {pending.length === 0 && <p style={{ textAlign: 'center', color: 'var(--text-mut)', fontSize: 13, padding: '24px 0' }}>No tienes tareas pendientes.</p>}
            {pending.map((task) => (
              <button key={task.id} onClick={() => { t.setTask(task.id); setShowPicker(false); }} style={pickerRow}>
                <div style={{ width: 3, height: 22, borderRadius: 3, background: PRIORITY_COLORS[task.priority], flexShrink: 0 }} />
                <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.name}</span>
                <span className="chip" style={{ background: 'var(--fill)', color: 'var(--text-mut)' }}>{task.completedPomodoros}/{task.estimatedPomodoros}</span>
                {t.taskId === task.id && <Check size={16} color="var(--primary)" />}
              </button>
            ))}
          </div>
        </Sheet>
      )}
    </div>
  );
}

const pickerRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 11, width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', padding: '12px 8px', borderRadius: 10, fontSize: 14, color: 'var(--text-pri)' };
